import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Megaphone, Send, Loader2, Users, Building2, Info, CheckCircle2,
  AlertTriangle, Bell, Clock, MessageSquare,
} from 'lucide-react';

const NOTIF_TYPES = [
  { id: 'info', label: 'Info', color: '#3b82f6' },
  { id: 'success', label: 'Success', color: '#22c55e' },
  { id: 'warning', label: 'Warning', color: '#f59e0b' },
  { id: 'error', label: 'Urgent', color: '#ef4444' },
];

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function TenantBroadcastPage() {
  const [tenants, setTenants] = useState([]);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Form state
  const [scope, setScope] = useState('all');
  const [tenantId, setTenantId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notifType, setNotifType] = useState('info');
  const [actionUrl, setActionUrl] = useState('');

  const fetchAll = async () => {
    try {
      const [t, h] = await Promise.all([
        api.get('/api/admin/tenant-broadcast/tenants'),
        api.get('/api/admin/tenant-broadcast/history?limit=25'),
      ]);
      setTenants(t.data.tenants || []);
      setTotalAdmins(t.data.total_tenant_admins || 0);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load broadcast data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const selectedTenant = tenants.find((t) => t.id === tenantId);
  const previewCount =
    scope === 'all' ? totalAdmins : selectedTenant?.admin_count || 0;
  const canSend = title.trim() && message.trim() && previewCount > 0 && !sending
    && (scope === 'all' || tenantId);

  const handleSend = async () => {
    if (!canSend) return;
    if (
      !window.confirm(
        `Send this broadcast to ${previewCount} tenant admin${previewCount === 1 ? '' : 's'}? This cannot be undone.`
      )
    ) {
      return;
    }
    setSending(true);
    try {
      await api.post('/api/admin/tenant-broadcast/send', {
        scope,
        tenant_id: scope === 'tenant' ? tenantId : null,
        title: title.trim(),
        message: message.trim(),
        notification_type: notifType,
        action_url: actionUrl.trim() || null,
      });
      toast.success(
        `Broadcast sent to ${previewCount} tenant admin${previewCount === 1 ? '' : 's'}`
      );
      setTitle('');
      setMessage('');
      setActionUrl('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="broadcast-loading">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-broadcast-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-[#D4AF37]" /> Broadcast to Tenants
        </h2>
        <p className="text-xs text-gray-400 mt-1">
          Push a platform announcement straight into every tenant admin's notification
          inbox. Use it for outages, new feature rollouts, policy updates — anything
          tenants must see fast.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Compose */}
        <div
          className="lg:col-span-3 rounded-2xl p-5 bg-[#131822] border border-white/10 space-y-4"
          data-testid="broadcast-compose"
        >
          <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#D4AF37]" /> Compose Broadcast
          </h3>

          {/* Scope toggle */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
              Audience
            </label>
            <div className="flex gap-2" data-testid="scope-toggle">
              <button
                onClick={() => setScope('all')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition ${
                  scope === 'all'
                    ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                    : 'bg-[#0B0E14] text-gray-300 border-white/10 hover:border-white/20'
                }`}
                data-testid="scope-all-btn"
              >
                <Users className="w-3.5 h-3.5" />
                All tenants ({totalAdmins})
              </button>
              <button
                onClick={() => setScope('tenant')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition ${
                  scope === 'tenant'
                    ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                    : 'bg-[#0B0E14] text-gray-300 border-white/10 hover:border-white/20'
                }`}
                data-testid="scope-tenant-btn"
              >
                <Building2 className="w-3.5 h-3.5" />
                Specific tenant
              </button>
            </div>
          </div>

          {scope === 'tenant' && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                Choose tenant
              </label>
              <select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                className="w-full bg-[#0B0E14] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white"
                data-testid="tenant-select"
              >
                <option value="">— Select a tenant —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} · {t.slug} · {t.admin_count} admin{t.admin_count === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Scheduled maintenance tonight"
              className="w-full bg-[#0B0E14] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600"
              data-testid="broadcast-title-input"
            />
            <p className="text-[10px] text-gray-500 mt-1">{title.length}/200</p>
          </div>

          {/* Message */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
              Message <span className="text-red-400">*</span>
            </label>
            <textarea
              rows={5}
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write a short, clear message. Tenants will see this in their admin notifications panel."
              className="w-full bg-[#0B0E14] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 resize-y"
              data-testid="broadcast-message-input"
            />
            <p className="text-[10px] text-gray-500 mt-1">{message.length}/2000</p>
          </div>

          {/* Type + action url */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                Severity
              </label>
              <div className="grid grid-cols-4 gap-1" data-testid="notif-type-toggle">
                {NOTIF_TYPES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setNotifType(t.id)}
                    className={`px-2 py-2 rounded-lg text-[11px] font-semibold border transition ${
                      notifType === t.id
                        ? 'border-white/40 text-white'
                        : 'border-white/10 text-gray-400 hover:border-white/20'
                    }`}
                    style={{
                      background: notifType === t.id ? `${t.color}20` : 'transparent',
                    }}
                    data-testid={`notif-type-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-2">
                Action URL <span className="text-gray-600">(optional)</span>
              </label>
              <input
                type="text"
                value={actionUrl}
                onChange={(e) => setActionUrl(e.target.value)}
                placeholder="/admin/settings"
                className="w-full bg-[#0B0E14] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600"
                data-testid="broadcast-action-url"
              />
            </div>
          </div>

          {/* Send */}
          <div className="pt-2 flex items-center justify-between border-t border-white/5">
            <p className="text-xs text-gray-400 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" />
              Will reach <span className="font-semibold text-white">{previewCount}</span>{' '}
              tenant admin{previewCount === 1 ? '' : 's'}
            </p>
            <button
              onClick={handleSend}
              disabled={!canSend}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#D4AF37] text-black font-semibold hover:bg-[#F5D76E] disabled:opacity-40 disabled:cursor-not-allowed transition"
              data-testid="broadcast-send-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sending ? 'Sending…' : 'Send Broadcast'}
            </button>
          </div>
        </div>

        {/* Preview + History */}
        <div className="lg:col-span-2 space-y-4">
          <div
            className="rounded-2xl p-5 bg-gradient-to-br from-[#1a0f2e] to-[#0B0E14] border border-white/10"
            data-testid="broadcast-preview"
          >
            <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#D4AF37]" /> Live Preview
            </h3>
            {title || message ? (
              <div className="rounded-xl bg-[#131822] border border-white/10 p-3">
                <div className="flex items-start gap-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${
                        NOTIF_TYPES.find((t) => t.id === notifType)?.color || '#3b82f6'
                      }20`,
                    }}
                  >
                    {notifType === 'warning' && (
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                    )}
                    {notifType === 'success' && (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    )}
                    {notifType === 'error' && (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    )}
                    {notifType === 'info' && <Info className="w-4 h-4 text-blue-400" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">
                      {title || 'Your title here'}
                    </p>
                    <p className="text-xs text-gray-300 mt-1 whitespace-pre-wrap">
                      {message || 'Your message will appear here…'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">
                Start typing to see a preview of what tenant admins will receive.
              </p>
            )}
          </div>

          <div
            className="rounded-2xl p-5 bg-[#131822] border border-white/10"
            data-testid="broadcast-history"
          >
            <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" /> Recent Broadcasts
            </h3>
            {history.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                No broadcasts sent yet. Your first broadcast will appear here.
              </p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {history.map((h) => (
                  <div
                    key={h.id}
                    className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5"
                    data-testid={`broadcast-history-${h.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-white truncate">{h.title}</p>
                      <span className="text-[10px] text-gray-500 flex-shrink-0">
                        {h.recipients_count} recipient{h.recipients_count === 1 ? '' : 's'}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1.5">
                      <span className="capitalize">
                        {h.scope === 'all' ? 'All tenants' : h.tenant_name || 'Single tenant'}
                      </span>
                      <span className="text-gray-600">·</span>
                      <span>{fmtTime(h.created_at)}</span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
